import { useEffect, useRef } from 'react'

const BGM_VOLUME = 0.3
const CLICK_VOLUME = 0.35
const MUTE_KEY = 'ai_game_muted'

/** 页面 BGM（循环、30% 音量、首次交互后播放）+ 点击音效 */
export default function AudioFx() {
  const bgmRef = useRef<HTMLAudioElement | null>(null)
  const clickRef = useRef<HTMLAudioElement | null>(null)
  const startedRef = useRef(false)

  useEffect(() => {
    const muted = localStorage.getItem(MUTE_KEY) === '1'

    const bgm = new Audio('/audio/bgm.mp3')
    bgm.loop = true
    bgm.volume = BGM_VOLUME
    bgm.preload = 'auto'
    bgm.muted = muted
    bgmRef.current = bgm

    const click = new Audio('/audio/click.mp3')
    click.volume = CLICK_VOLUME
    click.preload = 'auto'
    click.muted = muted
    clickRef.current = click

    const startBgm = () => {
      if (startedRef.current) return
      startedRef.current = true
      bgm.play().catch(() => {
        /* 自动播放被拦截时保持静默，等待下一次手势 */
      })
    }

    const onGesture = () => {
      startBgm()
      window.removeEventListener('pointerdown', onGesture)
      window.removeEventListener('keydown', onGesture)
    }

    // 尝试立即播放；被拦截则在首次点击/按键后开始
    bgm.play().then(() => {
      startedRef.current = true
    }).catch(() => {
      window.addEventListener('pointerdown', onGesture, { once: true })
      window.addEventListener('keydown', onGesture, { once: true })
    })

    let lastClickAt = 0
    const onClick = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      const interactive = target.closest('button, a, .btn, input, select, textarea, .list-item')
      if (!interactive) return
      if (localStorage.getItem(MUTE_KEY) === '1') return
      const now = Date.now()
      if (now - lastClickAt < 90) return
      lastClickAt = now
      try {
        click.currentTime = 0
        void click.play()
      } catch {
        /* ignore */
      }
    }

    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== MUTE_KEY) return
      const m = ev.newValue === '1'
      bgm.muted = m
      click.muted = m
    }

    window.addEventListener('pointerdown', onClick, { passive: true })
    window.addEventListener('storage', onStorage)

    return () => {
      window.removeEventListener('pointerdown', onClick)
      window.removeEventListener('pointerdown', onGesture)
      window.removeEventListener('keydown', onGesture)
      window.removeEventListener('storage', onStorage)
      bgm.pause()
      bgmRef.current = null
      clickRef.current = null
    }
  }, [])

  return null
}

/** 顶栏静音开关（同步 localStorage + 广播 storage 事件） */
export function toggleMute(): boolean {
  const next = localStorage.getItem(MUTE_KEY) !== '1'
  localStorage.setItem(MUTE_KEY, next ? '1' : '0')
  window.dispatchEvent(new StorageEvent('storage', { key: MUTE_KEY, newValue: next ? '1' : '0' }))
  return next
}

export function isMuted(): boolean {
  return localStorage.getItem(MUTE_KEY) === '1'
}
